import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first with Chrome-compatible error handling
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }
    } catch (error) {
      // LocalStorage blocked (incognito mode, privacy settings, etc.)
      console.warn('localStorage access blocked:', error);
    }

    // Fall back to system preference
    if (typeof window !== 'undefined') {
      try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } catch (error) {
        console.warn('matchMedia not available:', error);
      }
    }
    return 'light'; // Default fallback
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Safely save to localStorage with Chrome compatibility
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      // Silently fail if localStorage is blocked - theme will still work via state
      console.warn('Could not save theme to localStorage:', error);
    }
  }, [theme]);

  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        try {
          // Only auto-switch if user hasn't manually set a theme
          const savedTheme = localStorage.getItem('theme');
          if (!savedTheme) {
            setTheme(e.matches ? 'dark' : 'light');
          }
        } catch (error) {
          // If localStorage is blocked, still allow system preference changes
          setTheme(e.matches ? 'dark' : 'light');
        }
      };

      // Use the correct method for both Chrome and Safari
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } else if ((mediaQuery as any).addListener) {
        // Fallback for older browsers
        (mediaQuery as any).addListener(handleChange);
        return () => (mediaQuery as any).removeListener(handleChange);
      }
    } catch (error) {
      console.warn('Could not set up theme media query listener:', error);
    }
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('theme', newTheme);
      } catch (error) {
        console.warn('Could not save theme preference:', error);
      }
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};